import WebApp from '@twa-dev/sdk'

export const PRIVACY_POLICY_VERSION = '2026-08-31-v3'
const API_BASE_URL = import.meta.env.VITE_API_URL || ''
const DEV_CONSENT_KEY = 'web3_miniapp_privacy_consent_dev'

export interface PrivacyConsentStatus {
  accepted: boolean
  required: boolean
  current_version: string
  accepted_at?: string | null
}

export interface PrivacyDeletionResult {
  deleted: boolean
  deleted_files: number
  details: Record<string, number>
}

interface ApiErrorPayload {
  detail?: string
  code?: string
  current_version?: string
  submitted_version?: string
}

export class PrivacyApiError extends Error {
  status: number
  code?: string
  currentVersion?: string
  submittedVersion?: string

  constructor(status: number, payload: ApiErrorPayload) {
    super(payload.detail || `HTTP ${status}`)
    this.name = 'PrivacyApiError'
    this.status = status
    this.code = payload.code
    this.currentVersion = payload.current_version
    this.submittedVersion = payload.submitted_version
  }
}

export function isPrivacyVersionCompatible(currentVersion: string): boolean {
  return currentVersion === PRIVACY_POLICY_VERSION
}

function developmentStatus(): PrivacyConsentStatus {
  const accepted = import.meta.env.DEV && localStorage.getItem(DEV_CONSENT_KEY) === PRIVACY_POLICY_VERSION
  return {
    accepted,
    required: !accepted,
    current_version: PRIVACY_POLICY_VERSION,
    accepted_at: accepted ? new Date().toISOString() : null,
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  if (!WebApp.initData && import.meta.env.DEV) {
    if (path.endsWith('/privacy-data')) {
      localStorage.removeItem(DEV_CONSENT_KEY)
      return { deleted: true, deleted_files: 0, details: {} } as T
    }
    if (init.method === 'POST') localStorage.setItem(DEV_CONSENT_KEY, PRIVACY_POLICY_VERSION)
    if (init.method === 'DELETE') localStorage.removeItem(DEV_CONSENT_KEY)
    return developmentStatus() as T
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(WebApp.initData ? { 'X-TG-Init-Data': WebApp.initData } : {}),
      ...(init.headers || {}),
    },
  })
  if (!response.ok) {
    const payload = await response.json().catch(() => ({ detail: `HTTP ${response.status}` })) as ApiErrorPayload
    throw new PrivacyApiError(response.status, payload)
  }
  return response.json()
}

export const privacyApi = {
  status: () => request<PrivacyConsentStatus>('/api/user/privacy-consent'),
  accept: () => request<PrivacyConsentStatus>('/api/user/privacy-consent', {
    method: 'POST',
    body: JSON.stringify({ consent: true, policy_version: PRIVACY_POLICY_VERSION }),
  }),
  withdraw: () => request<PrivacyConsentStatus>('/api/user/privacy-consent', { method: 'DELETE' }),
  deleteData: () => request<PrivacyDeletionResult>('/api/user/privacy-data', {
    method: 'DELETE',
    body: JSON.stringify({ confirmation: 'DELETE' }),
  }),
}
